import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import type { AccionesRol } from 'modelos';
import { SesionStore } from '../sesion/sesion.store';
import { PermisosService } from './permisos.service';
import type { VistaFaro } from './vistas';

/**
 * Guards funcionales (`CanActivateFn`), el idioma de Angular 22.
 *
 * A diferencia de IRIX —donde casi todas las rutas tienen `canActivate: []` y el
 * control es sólo visual en la plantilla— en FARO **las rutas se protegen de
 * verdad**. Es barato hacerlo desde el día 1 y evita que un link pegado a mano
 * muestre una pantalla vacía con 403 en la consola.
 */

/** Hay sesión. Si no, al login, recordando a dónde quería ir. */
export const sesionGuard: CanActivateFn = (_ruta, estado) => {
  const store = inject(SesionStore);
  const router = inject(Router);

  if (store.logueado()) return true;
  return router.createUrlTree(['/login'], { queryParams: { volverA: estado.url } });
};

/**
 * El módulo de Luminarias está habilitado: en el permiso activo
 * (`modulos.moduloLuminarias`) y en el cliente (`config.moduloLuminarias.activo`).
 * Nivel 0 entra siempre. Es `tieneModulo('Luminarias')` de IRIX.
 *
 * Es el único módulo que decide si FARO sirve para este usuario: acá no hay
 * navegación automática por módulos como en IRIX, porque hay un módulo solo.
 */
export const alumbradoGuard: CanActivateFn = () => {
  const permisos = inject(PermisosService);
  const router = inject(Router);

  if (permisos.tieneModulo('Luminarias')) return true;
  return router.createUrlTree(['/sin-acceso']);
};

/**
 * La ruta declara qué acciones hacen falta en `data.acciones`.
 *
 * ```ts
 * {
 *   path: 'luminarias/nueva',
 *   loadComponent: () => import('./alta-edicion/crear-editar-luminaria.component'),
 *   canActivate: [accionesGuard],
 *   data: { acciones: ['Luminarias - Crear'] satisfies AccionesRol[] },
 * }
 * ```
 */
export const accionesGuard: CanActivateFn = (ruta) => {
  const permisos = inject(PermisosService);
  const router = inject(Router);

  const requeridas = (ruta.data['acciones'] ?? []) as AccionesRol[];
  if (!requeridas.length) return true;
  if (permisos.tienePermiso(...requeridas)) return true;

  return router.createUrlTree(['/sin-permiso'], {
    queryParams: { acciones: requeridas.join(', ') },
  });
};

/**
 * La ruta declara qué pantalla es en `data.pantalla`, y se entra si
 * `PermisosService.puedeVer` lo permite (acciones, módulo, nivel 0 o puestas,
 * según `VISTAS`). Es el mismo requisito que usa el menú para mostrarla.
 *
 * Si la pantalla depende de un parámetro (los grupos de luminarias y de puestas
 * comparten ruta), `data.pantalla` puede ser una función de la ruta.
 */
export type PantallaDeRuta = VistaFaro | ((ruta: ActivatedRouteSnapshot) => VistaFaro);

export const vistaGuard: CanActivateFn = (ruta) => {
  const permisos = inject(PermisosService);
  const router = inject(Router);

  const pantalla = ruta.data['pantalla'] as PantallaDeRuta | undefined;
  const vista = typeof pantalla === 'function' ? pantalla(ruta) : pantalla;
  if (!vista || permisos.puedeVer(vista)) return true;
  return router.createUrlTree(['/sin-permiso']);
};
